import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { questionDefinitions } from "../data/questionGenerator";

export const QuizContext = createContext();

export const QuizProvider = ({ children }) => {
  // No default answers - start with empty state
  const defaultAnswers = useMemo(() => ({}), []);

  const [answers, setAnswers] = useState(defaultAnswers);
  const [currentQuestion, setCurrentQuestion] = useState("q1");
  const [sessionId, setSessionId] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  const generateSessionId = () => {
    return `elisa-quiz-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  };

  const loadSavedState = useCallback(() => {
    try {
      const savedState = localStorage.getItem("elisaQuizState");
      if (savedState) {
        const state = JSON.parse(savedState);

        // Check if session is not too old (24 hours)
        const age = Date.now() - state.timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours

        if (age < maxAge && state.answers) {
          setAnswers(state.answers);
          setCurrentQuestion(state.currentQuestion || "q1");
          setIsComplete(state.isComplete || false);
          return true; // State was restored
        }
      }
    } catch (error) {
      console.warn("Failed to load saved quiz state:", error);
    }

    // If no state restored, use default answers
    setAnswers(defaultAnswers);
    return false; // No state restored, using defaults
  }, [defaultAnswers]);

  // Initialize session ID and load saved state
  useEffect(() => {
    const id = generateSessionId();
    setSessionId(id);
    // Enable state restoration from localStorage
    const restored = loadSavedState();
    if (!restored) {
      console.warn("Quiz state could not be restored. Starting fresh.");
    }
  }, [loadSavedState]);

  // Auto-save state to localStorage whenever answers change
  useEffect(() => {
    const saveStateToStorage = () => {
      const state = {
        sessionId,
        answers,
        currentQuestion,
        timestamp: Date.now(),
        isComplete,
      };

      try {
        localStorage.setItem("elisaQuizState", JSON.stringify(state));
        localStorage.setItem("elisaQuizHeartbeat", Date.now().toString());
      } catch (error) {
        console.warn("Failed to save quiz state to localStorage:", error);
      }
    };

    if (sessionId) {
      saveStateToStorage();
    }
  }, [answers, sessionId, currentQuestion, isComplete]);

  const setAnswer = (questionKey, value) => {
    setAnswers((prev) => {
      const newAnswers = { ...prev };

      // Clear the current question if value is null/undefined
      if (value === null || value === undefined) {
        delete newAnswers[questionKey];
      } else {
        newAnswers[questionKey] = Array.isArray(value) ? value : [value];
      }

      // Clear all subsequent questions (matching Angular behavior)
      // Extract question number from questionKey (e.g., "q1" -> 1)
      const currentQuestionNumber = parseInt(questionKey.substring(1));
      if (!isNaN(currentQuestionNumber)) {
        // Clear any question with a higher number
        Object.keys(newAnswers).forEach((key) => {
          const questionNumber = parseInt(key.substring(1));
          if (
            !isNaN(questionNumber) &&
            questionNumber > currentQuestionNumber
          ) {
            delete newAnswers[key];
          }
        });
      }

      return newAnswers;
    });
  };

  const getAnswer = (questionKey) => {
    return answers[questionKey] || null;
  };

  const clearAnswer = (questionKey) => {
    setAnswers((prev) => {
      const newAnswers = { ...prev };
      delete newAnswers[questionKey];
      return newAnswers;
    });
  };

  const clearAllAnswers = () => {
    setAnswers({});
    setCurrentQuestion("q1");
    setIsComplete(false);
    localStorage.removeItem("elisaQuizState");
    localStorage.removeItem("elisaQuizHeartbeat");
  };

  const checkCompletion = () => {
    // Check if all 6 questions are answered, treating 'unsure' as valid for any question with allow_skip: true
    const questionKeys = ["q1", "q2", "q3", "q4", "q5", "q6"];
    // Dynamically get allow_skip from questionDefinitions
    const answeredRequired = questionKeys.every((q) => {
      const answer = answers[q];
      const allowSkip =
        typeof questionDefinitions[q]?.allow_skip === "boolean"
          ? questionDefinitions[q].allow_skip
          : false;
      if (!answer || answer.length === 0) return false;
      if (allowSkip && answer[0] === "unsure") return true;
      return answer[0] !== "" && answer[0] !== undefined;
    });

    setIsComplete(answeredRequired);
    return answeredRequired;
  };

  const getProgressPercentage = () => {
    const totalQuestions = 6;
    const answeredQuestions = Object.keys(answers).filter(
      (key) => answers[key] && answers[key].length > 0
    ).length;
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  // Generate answer combination key for product mapping
  const getAnswerKey = () => {
    const keys = ["q1", "q2", "q3", "q4", "q5", "q6"];
    return keys
      .map((key) => {
        const answer = answers[key];
        return answer && answer.length > 0 ? answer[0] : "any";
      })
      .join("-");
  };

  const isQuestionOptionVisible = (question, option, answers, data) => {
    let visible = true;

    if (data) {
      const query = Object.keys(answers).reduce((acc, key) => {
        if (key < question.key) {
          acc[key] = answers[key][0];
        }
        return acc;
      }, {});

      const remainingData = data.filter((row) => {
        return Object.keys(query).every((key) => {
          const value = query[key];
          return row[key] === value || row[key] === "*";
        });
      });

      const remainingOptions = remainingData.map((row) => row[question.key]);
      visible = remainingOptions.includes(option.key);
    }

    return visible;
  };

  const resetToDefaults = () => {
    setAnswers(defaultAnswers);
    setCurrentQuestion("q1");
    setIsComplete(false);
  };

  const value = {
    // State
    answers,
    currentQuestion,
    sessionId,
    isComplete,

    // Actions
    setAnswer,
    getAnswer,
    clearAnswer,
    clearAllAnswers,
    resetToDefaults,
    setCurrentQuestion,

    // Utilities
    checkCompletion,
    getProgressPercentage,
    getAnswerKey,
    saveStateToStorage: () => {
      const state = {
        sessionId,
        answers,
        currentQuestion,
        timestamp: Date.now(),
        isComplete,
      };

      try {
        localStorage.setItem("elisaQuizState", JSON.stringify(state));
        localStorage.setItem("elisaQuizHeartbeat", Date.now().toString());
      } catch (error) {
        console.warn("Failed to save quiz state to localStorage:", error);
      }
    },
    loadSavedState,
    isQuestionOptionVisible,
  };

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
};

export const useQuiz = () => {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error("useQuiz must be used within a QuizProvider");
  }
  return context;
};
